// src/pages/Reports/ReportRismMembership.jsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Download } from "lucide-react";

export default function ReportRismMembership() {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [errorYears, setErrorYears] = useState(null);
  const [errorRows, setErrorRows] = useState(null);

  // Load distinct enrollment years (not deleted), newest first
  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoadingYears(true);
      setErrorYears(null);
      try {
        // Pull only the field we need; Supabase doesn't have DISTINCT helper,
        // so fetch and unique in JS to keep this page self-contained.
        const { data, error } = await supabase
          .from("students")
          .select("enrollment_year, is_deleted")
          .order("enrollment_year", { ascending: false });

        if (error) throw error;

        const uniq = Array.from(
          new Set((data || []).filter(r => !r.is_deleted).map(r => r.enrollment_year))
        ).sort((a, b) => b - a);

        if (!ignore) {
          setYears(uniq);
          // Auto-select the newest year available
          if (!selectedYear && uniq.length > 0) {
            setSelectedYear(String(uniq[0]));
          }
        }
      } catch (e) {
        if (!ignore) setErrorYears(e.message || "Failed to load years");
      } finally {
        if (!ignore) setLoadingYears(false);
      }
    })();
    return () => { ignore = true; };
  }, []); // load once

  // Load students for selected year
  useEffect(() => {
    if (!selectedYear) return;
    let ignore = false;
    (async () => {
      setLoadingRows(true);
      setErrorRows(null);
      try {
        const { data, error } = await supabase
          .from("students")
          .select("id, full_name, contact_no, email, membership_status, remarks, is_deleted, enrollment_year")
          .eq("enrollment_year", Number(selectedYear))
          .eq("is_deleted", false)
          .order("full_name", { ascending: true });

        if (error) throw error;
        if (!ignore) setRows(data || []);
      } catch (e) {
        if (!ignore) setErrorRows(e.message || "Failed to load students");
      } finally {
        if (!ignore) setLoadingRows(false);
      }
    })();
    return () => { ignore = true; };
  }, [selectedYear]);

  // Small Chip component (mirrors ReportIntake.jsx tone/styles)
  const Chip = ({ children, tone = "indigo" }) => (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs
      ${tone === "indigo" ? "border-indigo-200 bg-indigo-50 text-indigo-700" : ""}
      ${tone === "sky" ? "border-sky-200 bg-sky-50 text-sky-700" : ""}
      ${tone === "slate" ? "border-slate-200 bg-slate-50 text-slate-700" : ""}`}
    >
      {children}
    </span>
  );

  // Helpers for CSV export (reusing the same patterns from ReportIntake.jsx)
  const todayKL = useMemo(() => {
    const y = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    const m = new Intl.DateTimeFormat("en-GB", {
      month: "2-digit",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    const d = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    return `${y}${m}${d}`; // YYYYMMDD
  }, []);

  const csvEscape = (val) => {
    const s = val == null ? "" : String(val);
    if (/[",\n]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const handleExportCsv = () => {
    const headers = [
      "No.",
      "Student Name",
      "Contact Number",
      "Email Address",
      "RISM Membership Status",
      "Remarks",
    ];
    const lines = [headers.map(csvEscape).join(",")];

    rows.forEach((row, idx) => {
      lines.push(
        [
          idx + 1,
          row.full_name || "",
          row.contact_no || "",
          row.email || "",
          row.membership_status || "",
          row.remarks || "",
        ].map(csvEscape).join(",")
      );
    });

    const csv = lines.join("\n");
    const filename = `excel_rism_membership_${selectedYear || "year"}_${todayKL}.csv`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportEnabled = rows.length > 0 && !!selectedYear;

  return (
    <div className="p-2 md:p-2">
      {/* Header card with gradient accent (matches ReportIntake.jsx) */}
      <div className="mb-4">
        <div className="relative overflow-hidden rounded-xl border border-slate-200 shadow-sm">
          <div className="h-2 w-full bg-gradient-to-r from-indigo-500 via-sky-400 to-cyan-400" />
          <div className="bg-white p-4 md:p-5">
            <h1 className="text-xl font-semibold text-slate-800">
              Student RISM Membership Report
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Filter by enrollment year. Click a student to view details.
            </p>

            {/* Live filter chips */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Chip tone="indigo">Enrollment Year: {selectedYear || "-"}</Chip>
              <Chip tone="slate">Rows: {rows.length}</Chip>
            </div>

            {/* Filter + Export controls */}
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[14rem,1fr] md:items-end">
              {/* Year selector */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Enrollment Year
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  disabled={loadingYears || !!errorYears}
                >
                  {loadingYears && <option>Loading…</option>}
                  {!loadingYears && years.length === 0 && (
                    <option>No years found</option>
                  )}
                  {!loadingYears &&
                    years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                </select>
                {errorYears && (
                  <p className="mt-1 text-xs text-red-600">
                    Failed to load years: {errorYears}
                  </p>
                )}
              </div>

              {/* Export */}
              <div className="flex justify-start md:justify-end">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  disabled={!exportEnabled}
                  title="Export what’s on screen"
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium shadow-sm
                    ${
                      exportEnabled
                        ? "bg-sky-600 text-white hover:bg-sky-700"
                        : "bg-slate-100 text-slate-400 cursor-not-allowed"
                    }`}
                >
                  <Download className="h-4 w-4" strokeWidth={2.5} />
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Card */}
      <div className="mt-4 bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="p-3 md:p-4">
          {!selectedYear ? (
            <div className="text-xs text-slate-500">
              Select an enrollment year to view membership data.
            </div>
          ) : loadingRows ? (
            <div className="text-xs text-slate-500">Loading students…</div>
          ) : errorRows ? (
            <div className="text-xs text-red-600">
              Failed to load data: {errorRows}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-slate-500">
              No students found for {selectedYear}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-left text-slate-600 border-b">
                    <th className="py-2 pr-2">No.</th>
                    <th className="py-2 pr-2">Student Name</th>
                    <th className="py-2 pr-2">Contact Number</th>
                    <th className="py-2 pr-2">Email Address</th>
                    <th className="py-2 pr-2">RISM Membership Status</th>
                    <th className="py-2 pr-2">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr
                      key={row.id}
                      className={`border-b last:border-0 ${
                        idx % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                      } hover:bg-sky-50/60`}
                    >
                      <td className="py-2 pr-2">{idx + 1}</td>
                      <td className="py-2 pr-2">
                        <Link
                          to={{ pathname: `/students/${row.id}`, search: "?from=report" }}
                          state={{ from: "report" }}
                          className="font-medium text-slate-800 hover:text-sky-700 hover:underline"
                        >
                          {row.full_name}
                        </Link>
                      </td>
                      <td className="py-2 pr-2">{row.contact_no}</td>
                      <td className="py-2 pr-2">{row.email}</td>
                      <td className="py-2 pr-2">{row.membership_status}</td>
                      <td className="py-2 pr-2">{row.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 text-[11px] text-slate-500">
                Showing {rows.length} students • Enrollment Year: {selectedYear}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
